import React, { useState, useEffect, useRef } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import formStyles from '../../styles/SmartSendForm.module.css';

// Constants
const IFRAME_LOAD_TIMEOUT_MS = 10000; // 10 seconds
const HEIGHT_CALCULATION_DELAY_MS = 100; // Delay for content to render
const MIN_HEIGHT_THRESHOLD = 50; // Minimum height threshold for fallback (lowered from 100)

/**
 * PersonalMessageStep - Displays personal message in iframe after phone verification
 * Shows personal message with preview=true to hide footer, then allows user to continue
 *
 * @param {string} pmId - Personal message ID from order data
 * @param {Function} onContinue - Handler for continuing to address selection
 */
const PersonalMessageStep = ({ pmId, onContinue }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isHeightCalculated, setIsHeightCalculated] = useState(false);
  const [areBackgroundsLoaded, setAreBackgroundsLoaded] = useState(false);
  const iframeRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const wrapperRef = useRef(null);
  const heightUpdateTimeoutRef = useRef(null);
  const heightCheckIntervalRef = useRef(null);
  const intervalClearTimeoutRef = useRef(null);
  const areBackgroundsLoadedRef = useRef(false);

  // Validate pmId
  const isValidPmId = pmId && typeof pmId === 'string' && pmId.trim() !== '';

  // Construct messages URL with preview=true to hide footer
  const messagesUrl = isValidPmId
    ? `/messages?tokenId=${encodeURIComponent(pmId.trim())}&preview=true`
    : null;

  // Initialize CSS custom properties on mount
  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.style.setProperty('--iframe-height', '1px');
      wrapperRef.current.style.setProperty('--iframe-visibility', 'hidden');
      wrapperRef.current.style.setProperty('--iframe-opacity', '0');
    }
  }, []);

  /**
   * Waits for all background images in the iframe to load
   * Prevents text from showing before backgrounds are ready
   * @param {Document} iframeDocument - The iframe's document object
   * @returns {Promise<void>} - Resolves when all background images are loaded
   */
  const waitForBackgroundImages = (iframeDocument) => {
    return new Promise((resolve) => {
      try {
        // Find all elements with background images
        const allElements = iframeDocument.querySelectorAll('*');
        const backgroundImagePromises = [];

        allElements.forEach((element) => {
          // Check computed styles first
          const styles = iframeDocument.defaultView.getComputedStyle(element);
          let backgroundImage = styles.backgroundImage;

          // Also check inline style attribute (may override computed styles)
          if (element.style && element.style.backgroundImage) {
            backgroundImage = element.style.backgroundImage;
          }

          // Check if element has a background image (not 'none')
          if (backgroundImage && backgroundImage !== 'none' && backgroundImage !== 'initial') {
            // Extract URL from background-image CSS value
            // Handles formats like: url("..."), url('...'), url(...)
            const urlMatch = backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/);
            if (urlMatch && urlMatch[1]) {
              let imageUrl = urlMatch[1];
              
              // Resolve relative URLs to absolute
              if (imageUrl.startsWith('/')) {
                imageUrl = `${window.location.origin}${imageUrl}`;
              } else if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://') && !imageUrl.startsWith('data:')) {
                // Relative URL - resolve against current window origin
                try {
                  imageUrl = new URL(imageUrl, window.location.origin).href;
                } catch (e) {
                  // If URL resolution fails, use as-is (might be a data URL or special format)
                  console.warn('Could not resolve image URL:', imageUrl);
                }
              }

              // Create a promise that resolves when this image loads
              const imagePromise = new Promise((imgResolve) => {
                const img = new Image();
                img.onload = () => imgResolve();
                img.onerror = () => imgResolve(); // Resolve even on error to not block
                img.src = imageUrl;
              });
              backgroundImagePromises.push(imagePromise);
            }
          }
        });

        // If no background images found, resolve immediately
        if (backgroundImagePromises.length === 0) {
          resolve();
          return;
        }

        // Wait for all background images to load (or timeout after 3 seconds)
        const timeoutPromise = new Promise((timeoutResolve) => {
          setTimeout(() => timeoutResolve(), 3000);
        });

        Promise.race([
          Promise.all(backgroundImagePromises),
          timeoutPromise,
        ]).then(() => {
          resolve();
        });
      } catch (error) {
        console.warn('Error waiting for background images:', error);
        // Resolve on error to not block the UI
        resolve();
      }
    });
  };

  /**
   * Calculates the height of the iframe content based on actual rendered content
   * Uses multiple measurement strategies with fallbacks for accuracy
   * @param {Document} iframeDocument - The iframe's document object
   * @returns {number} - Calculated height in pixels
   */
  const calculateIframeHeight = (iframeDocument) => {
    try {
      const messageCardContainer = iframeDocument.querySelector('.message-card-container');
      const postCardMainContainer = iframeDocument.querySelector('.post-card-main-container');

      let measuredHeight = 0;

      // Find first and last elements for precise measurement
      let firstElement = null;
      let lastElement = null;
      if (postCardMainContainer) {
        const children = postCardMainContainer.children;
        if (children.length > 0) {
          firstElement = children[0];
          lastElement = children[children.length - 1];
        }
      } else if (messageCardContainer) {
        const children = messageCardContainer.children;
        if (children.length > 0) {
          firstElement = children[0];
          lastElement = children[children.length - 1];
        }
      }

      // Most precise measurement: from first element top to last element bottom
      if (firstElement && lastElement && firstElement !== lastElement) {
        const firstRect = firstElement.getBoundingClientRect();
        const lastRect = lastElement.getBoundingClientRect();
        // Measure from top of first element to bottom of last element
        const contentHeight = lastRect.bottom - firstRect.top;

        // Get computed styles to check for any remaining margins/padding
        const lastStyles = iframeDocument.defaultView.getComputedStyle(lastElement);
        const lastMarginBottom = parseFloat(lastStyles.marginBottom) || 0;
        const lastPaddingBottom = parseFloat(lastStyles.paddingBottom) || 0;

        // Also check container for any padding that might add space
        const container = postCardMainContainer || messageCardContainer;
        const containerStyles = container ? iframeDocument.defaultView.getComputedStyle(container) : null;
        const containerPaddingBottom = containerStyles ? parseFloat(containerStyles.paddingBottom) || 0 : 0;
        const containerMarginBottom = containerStyles ? parseFloat(containerStyles.marginBottom) || 0 : 0;

        // Subtract all remaining bottom spacing
        measuredHeight = Math.ceil(contentHeight - lastMarginBottom - lastPaddingBottom - containerPaddingBottom - containerMarginBottom);
      } else if (lastElement) {
        // Fallback: measure from container top to last element bottom
        const lastRect = lastElement.getBoundingClientRect();
        const containerRect = postCardMainContainer
          ? postCardMainContainer.getBoundingClientRect()
          : messageCardContainer?.getBoundingClientRect();

        if (containerRect) {
          const lastStyles = iframeDocument.defaultView.getComputedStyle(lastElement);
          const lastMarginBottom = parseFloat(lastStyles.marginBottom) || 0;
          const lastPaddingBottom = parseFloat(lastStyles.paddingBottom) || 0;

          // Also check container padding
          const container = postCardMainContainer || messageCardContainer;
          const containerStyles = container ? iframeDocument.defaultView.getComputedStyle(container) : null;
          const containerPaddingBottom = containerStyles ? parseFloat(containerStyles.paddingBottom) || 0 : 0;
          const containerPaddingTop = containerStyles ? parseFloat(containerStyles.paddingTop) || 0 : 0;

          // Measure from container content top (accounting for padding) to last element bottom
          const bottomOffset = lastRect.bottom - containerRect.top - containerPaddingTop;
          measuredHeight = Math.ceil(bottomOffset - lastMarginBottom - lastPaddingBottom - containerPaddingBottom);
        }
      }

      // Fallback: Use scrollHeight if element-based measurement failed
      if (measuredHeight === 0 || measuredHeight < MIN_HEIGHT_THRESHOLD) {
        if (postCardMainContainer) {
          measuredHeight = postCardMainContainer.scrollHeight;
        } else if (messageCardContainer) {
          measuredHeight = messageCardContainer.scrollHeight;
        }
      }

      // Final fallback to body scrollHeight
      if (measuredHeight === 0 || measuredHeight < MIN_HEIGHT_THRESHOLD) {
        measuredHeight = iframeDocument.body.scrollHeight;
      }

      return Math.ceil(measuredHeight);
    } catch (error) {
      console.warn('Error calculating iframe height:', error);
      return 0;
    }
  };

  // Handle iframe load events and errors
  useEffect(() => {
    if (!iframeRef.current || !messagesUrl) return;

    const iframe = iframeRef.current;
    let loadTimeout;

    const updateIframeHeight = () => {
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDocument && iframeDocument.body) {
          // Inject CSS first if not already done
          const styleId = 'preview-mode-override';
          if (!iframeDocument.getElementById(styleId)) {
            // Add preview-mode class first for selector specificity
            iframeDocument.documentElement.classList.add('preview-mode');
            if (iframeDocument.body) {
              iframeDocument.body.classList.add('preview-mode');
            }

            const style = iframeDocument.createElement('style');
            style.id = styleId;
            style.textContent = `
              /* Fix height issues - override 100vh styles that cause overflow */
              html.preview-mode {
                height: auto;
                min-height: auto;
                overflow-x: hidden;
              }

              /* Fix body height - ensure it fits content, not viewport */
              html.preview-mode body,
              html.preview-mode body.message-background,
              body.preview-mode,
              body.preview-mode.message-background {
                height: auto;
                min-height: auto;
                max-height: none;
                overflow-x: hidden;
              }

              /* Fix message-card-container to prevent viewport height issues */
              html.preview-mode .message-card-container {
                height: auto;
                min-height: auto;
                max-height: none;
                overflow-x: hidden;
              }

              /* CRITICAL: Remove 20px padding that causes horizontal scroll */
              /* Override width: fit-content to ensure proper scaling */
              html.preview-mode .post-card-main-container {
                padding: 0 !important;
                width: 100% !important;
                max-width: 100%;
                box-sizing: border-box;
              }

              /* Ensure post-card elements scale to fit container width */
              html.preview-mode .post-card-logo,
              html.preview-mode .post-card-names,
              html.preview-mode .post-card-selfie,
              html.preview-mode .post-card-message,
              html.preview-mode .post-card-doodle {
                max-width: 100%;
                width: 100%;
                min-width: 0;
              }

              /* Ensure images scale properly to fit container */
              html.preview-mode .post-card-logo-img,
              html.preview-mode .post-card-selfie-img,
              html.preview-mode .post-card-doodle-img,
              html.preview-mode .post-card-names-img {
                max-width: 100%;
                width: 100%;
                height: auto;
              }

              /* Hide message text until all assets are ready, then fade it in */
              html.preview-mode .message-to,
              html.preview-mode .message-from,
              html.preview-mode .message-text {
                opacity: 0;
                transition: opacity 200ms ease-in-out;
              }

              html.preview-mode.pm-content-ready .message-to,
              html.preview-mode.pm-content-ready .message-from,
              html.preview-mode.pm-content-ready .message-text {
                opacity: 1;
              }
            `;
            iframeDocument.head.appendChild(style);
          }

          // Calculate height using extracted helper function
          const newHeight = calculateIframeHeight(iframeDocument);
          if (newHeight > 0) {
            // Height is set via CSS custom property, no need to store in state
            setIsHeightCalculated(true);

            // Set CSS custom properties via ref to avoid inline styles
            // Only set visibility if backgrounds are also loaded
            if (wrapperRef.current) {
              wrapperRef.current.style.setProperty('--iframe-height', `${newHeight}px`);
              // Visibility will be set when backgrounds are loaded (checked via ref)
              if (areBackgroundsLoadedRef.current) {
                wrapperRef.current.style.setProperty('--iframe-visibility', 'visible');
              }
            }
          }
        }
      } catch (error) {
        // If we can't access iframe content (shouldn't happen for same-origin, but defensive)
        console.warn('Could not access iframe content for height calculation:', error);
      }
    };

    const handleLoad = async () => {
      setIsLoading(false);
      setHasError(false);
      if (loadTimeout) clearTimeout(loadTimeout);

      // Wait for background images to load before showing content
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDocument && iframeDocument.body) {
          await waitForBackgroundImages(iframeDocument);

          // Mark iframe document as ready so internal text can fade in
          try {
            iframeDocument.documentElement.classList.add('pm-content-ready');
            if (iframeDocument.body) {
              iframeDocument.body.classList.add('pm-content-ready');
            }
          } catch (e) {
            console.warn('Error setting pm-content-ready class on iframe document:', e);
          }

          areBackgroundsLoadedRef.current = true;
          setAreBackgroundsLoaded(true);

          // Set visibility now that backgrounds are loaded
          if (wrapperRef.current && isHeightCalculated) {
            wrapperRef.current.style.setProperty('--iframe-visibility', 'visible');
          }
        } else {
          // If we can't access iframe, show it anyway
          areBackgroundsLoadedRef.current = true;
          setAreBackgroundsLoaded(true);
        }
      } catch (error) {
        console.warn('Error waiting for background images:', error);
        // Show content even if background check fails
        areBackgroundsLoadedRef.current = true;
        setAreBackgroundsLoaded(true);
      }

      // Update height immediately and then again after a short delay to ensure content is rendered
      updateIframeHeight();

      // Clear any existing timeout before setting a new one
      if (heightUpdateTimeoutRef.current) {
        clearTimeout(heightUpdateTimeoutRef.current);
      }
      heightUpdateTimeoutRef.current = setTimeout(() => {
        updateIframeHeight();
        heightUpdateTimeoutRef.current = null;
      }, HEIGHT_CALCULATION_DELAY_MS);

      // Set up ResizeObserver to watch for content size changes
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDocument && iframeDocument.body) {
          // Use ResizeObserver to watch for size changes in the iframe content
          const resizeObserver = new ResizeObserver(() => {
            updateIframeHeight();
          });

          // Observe the body and main content containers
          resizeObserver.observe(iframeDocument.body);

          const messageCardContainer = iframeDocument.querySelector('.message-card-container');
          if (messageCardContainer) {
            resizeObserver.observe(messageCardContainer);
          }

          const postCardMainContainer = iframeDocument.querySelector('.post-card-main-container');
          if (postCardMainContainer) {
            resizeObserver.observe(postCardMainContainer);
          }

          // Store observer in ref for cleanup
          resizeObserverRef.current = resizeObserver;
        }
      } catch (error) {
        // If ResizeObserver isn't available or we can't access iframe content, fall back to polling
        console.warn('ResizeObserver not available, falling back to polling:', error);

        // Clear any existing interval and timeout before creating new ones
        if (heightCheckIntervalRef.current) {
          clearInterval(heightCheckIntervalRef.current);
        }
        if (intervalClearTimeoutRef.current) {
          clearTimeout(intervalClearTimeoutRef.current);
        }

        // Check for height changes periodically (content might load dynamically)
        heightCheckIntervalRef.current = setInterval(() => {
          updateIframeHeight();
        }, 500);

        // Clear interval after content should be loaded
        intervalClearTimeoutRef.current = setTimeout(() => {
          if (heightCheckIntervalRef.current) {
            clearInterval(heightCheckIntervalRef.current);
            heightCheckIntervalRef.current = null;
          }
          intervalClearTimeoutRef.current = null;
        }, 5000);
      }
    };

    const handleError = (e) => {
      // Iframe error events indicate load failures (network/CSP/etc.), not in-iframe JS errors
      // The browser sanitizes cross-origin JS errors to "Script error" for security,
      // but iframe error events are different - they mean the iframe failed to load
      e?.preventDefault?.();
      e?.stopPropagation?.();
      e?.stopImmediatePropagation?.();
      setIsLoading(false);
      setHasError(true); // Show error state for load failures
      if (loadTimeout) clearTimeout(loadTimeout);
      // Return false to prevent error from bubbling to window
      return false;
    };

    // Listen for postMessage from iframe (currently unused, kept for future communication)
    const handleMessage = (event) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) return;

      // Currently, no specific message types are handled here.
      // This listener is kept to allow for future postMessage-based communication with the iframe.
    };

    // Set timeout to handle cases where iframe doesn't load
    loadTimeout = setTimeout(() => {
      setIsLoading((prev) => {
        // Only clear loading if still loading (prev is true)
        if (prev) {
          // If iframe still hasn't loaded after timeout, show error state
          setHasError(true);
          return false;
        }
        return prev;
      });
    }, IFRAME_LOAD_TIMEOUT_MS);

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);
    window.addEventListener('message', handleMessage);

    return () => {
      if (loadTimeout) clearTimeout(loadTimeout);
      if (heightUpdateTimeoutRef.current) {
        clearTimeout(heightUpdateTimeoutRef.current);
        heightUpdateTimeoutRef.current = null;
      }
      if (heightCheckIntervalRef.current) {
        clearInterval(heightCheckIntervalRef.current);
        heightCheckIntervalRef.current = null;
      }
      if (intervalClearTimeoutRef.current) {
        clearTimeout(intervalClearTimeoutRef.current);
        intervalClearTimeoutRef.current = null;
      }
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      window.removeEventListener('message', handleMessage);

      // Clean up ResizeObserver if it was created
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [messagesUrl]);

  // Set visibility and opacity when both height is calculated and backgrounds are loaded
  useEffect(() => {
    if (isHeightCalculated && areBackgroundsLoaded && wrapperRef.current) {
      wrapperRef.current.style.setProperty('--iframe-visibility', 'visible');
      wrapperRef.current.style.setProperty('--iframe-opacity', '1');
    }
  }, [isHeightCalculated, areBackgroundsLoaded]);

  // If pmId is invalid, don't render (shouldn't happen, but defensive)
  if (!isValidPmId || !messagesUrl) {
    return null;
  }

  return (
    <div className={formStyles.verificationCard}>
      <h2 className={formStyles.sectionTitle}>Personal Message</h2>
      <p className={`${formStyles.note} ${formStyles.personalMessageNote}`}>
        A special message from your sender
      </p>

      {/* Loading State */}
      {isLoading && (
        <div
          className={formStyles.personalMessageLoadingContainer}
          aria-live="polite"
          aria-busy="true"
        >
          <ClipLoader size={50} color="#2B08A0" />
        </div>
      )}

      {/* Error State */}
      {hasError && !isLoading && (
        <div className={formStyles.personalMessageErrorContainer}>
          <p>Unable to load personal message.</p>
          <p className={formStyles.personalMessageErrorText}>
            You can continue to delivery details.
          </p>
        </div>
      )}

      {/* Personal Message Iframe */}
      {!hasError && (
        <div
          ref={wrapperRef}
          className={formStyles.personalMessageIframeWrapper}
        >
          <iframe
            ref={iframeRef}
            title="Personal Message"
            src={messagesUrl}
            width="100%"
            className={formStyles.personalMessageIframe}
            scrolling="no"
            allowFullScreen
            loading="lazy"
          />
        </div>
      )}

      {/* Continue Button */}
      <button
        type="button"
        onClick={onContinue}
        className={`${formStyles.button} ${formStyles.personalMessageContinueButton}`}
        aria-label="Continue to delivery address"
      >
        Continue
      </button>
    </div>
  );
};

export default PersonalMessageStep;
